import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { UserContextResponse, AuthState, LoginRequest } from '@apptypes/index';
import { authService } from '@services/auth.service';

interface AuthStore extends AuthState {
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  setUser: (user: UserContextResponse | null) => void;
  checkAuth: () => void;
  /** Check if user has a Chekit-level permission */
  hasCheKitPermission: (code: string) => boolean;
  /** Check if user has a business-level permission */
  hasBusinessPermission: (businessId: string, code: string) => boolean;
  /** Check either Chekit-level or business-level */
  hasPermission: (code: string, businessId?: string) => boolean;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => {
      // When api.ts silently refreshes the access token it fires a StorageEvent
      // so we can keep the Zustand token in sync without a circular import.
      if (typeof window !== 'undefined') {
        window.addEventListener('storage', (e) => {
          if (e.key === 'auth_token' && e.newValue) {
            set({ token: e.newValue });
          }
        });
      }

      return {
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,

      login: async (credentials: LoginRequest) => {
        set({ isLoading: true });
        try {
          const response = await authService.login(credentials);
          set({
            user: response.user,
            token: response.accessToken,
            refreshToken: response.refreshToken,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: () => {
        authService.logout();
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
        });
      },

      setUser: (user: UserContextResponse | null) => {
        set({ user, isAuthenticated: !!user });
      },

      checkAuth: () => {
        const token = authService.getToken();
        const user = authService.getCurrentUser();
        set({
          token,
          user,
          isAuthenticated: !!token && !!user,
        });
      },

      hasCheKitPermission: (code: string): boolean => {
        const { user } = get();
        if (!user?.chekit?.permissions) return false;
        // super_admin has all permissions via the role_permissions seed
        return user.chekit.permissions.includes(code);
      },

      hasBusinessPermission: (businessId: string, code: string): boolean => {
        const { user } = get();
        const biz = user?.businesses?.find((b) => b.businessId === businessId);
        return biz?.permissions?.includes(code) ?? false;
      },

      hasPermission: (code: string, businessId?: string): boolean => {
        const { hasCheKitPermission, hasBusinessPermission } = get();
        if (hasCheKitPermission(code)) return true;
        if (businessId) return hasBusinessPermission(businessId, code);
        return false;
      },
      };
    },
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
